import {
	createRootRouteWithContext,
	type ErrorComponentProps,
	HeadContent,
	Outlet,
	Scripts,
	useCanGoBack,
	useNavigate,
	useRouter
} from '@tanstack/react-router'
import { RefreshCw, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import '@/index.css'
import '@/lib/i18n'
import { Button } from '@repo/ui/button'
import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle
} from '@repo/ui/card'
import { ThemeProvider } from '@repo/ui/components/theme-provider'
import { Toaster } from '@repo/ui/sonner'
import { Spinner } from '@repo/ui/spinner'
import type { Theme } from '@repo/ui/theme'
import { TooltipProvider } from '@repo/ui/tooltip'
import { cn, formatError } from '@repo/ui/utils'
import { resolveBrand } from '@/lib/brand'
import i18n, { isAppLanguageCode, type TKey, translateApp } from '@/lib/i18n'
import { warmInstanceBranding } from '@/query/query-options'
import type { RouterContext } from '@/router'

export const Route = createRootRouteWithContext<RouterContext>()({
	// Keep the document shell SSR-capable so SSR profiles can render <html>,
	// head tags, request language, and first-paint theme setup. This does not
	// force child routes to SSR; routes without their own `ssr` still follow
	// the active profile's `defaultSsr`.
	ssr: true,
	beforeLoad: ({ context, serverContext }) => {
		return {
			// The language i18next is on right now, not the one the router was
			// built with. Route context is a snapshot taken once per tab and
			// `changeAppLanguage` does not write to it, so every `head()` that
			// reads it — this one, `admin/route.tsx`, `links/index.tsx` — kept
			// titling the tab in the language the tab had opened in while the
			// page below had already re-rendered in the newly picked one
			// (OPSSTD-8813). `router.tsx` invalidates the matches on each
			// change; this re-run is what then reads the new value.
			//
			// A request-rendered shell still wins: on the server the i18next
			// singleton is process-wide and knows nothing about this reader,
			// while `serverContext` was derived from this request's own cookie
			// and `Accept-Language`.
			appLanguage:
				serverContext?.appLanguage ??
				(isAppLanguageCode(i18n.resolvedLanguage)
					? i18n.resolvedLanguage
					: context.appLanguage),
			theme: serverContext?.theme ?? context.theme
		}
	},
	// Warms the instance branding where the server can read it (an SSR
	// profile), so the document title and the first HTML already carry the
	// reseller's name. Routes that render in the browser alone warm it in their
	// own loader; see `warmInstanceBranding`.
	loader: ({ context }) => warmInstanceBranding(context.queryClient),
	head: ({ loaderData, match }) => {
		const { productName, favicon } = resolveBrand(loaderData)

		return {
			meta: [
				{ charSet: 'utf-8' },
				{
					name: 'viewport',
					content: 'width=device-width, initial-scale=1.0'
				},
				// The tab title is the instance's product name, from the runtime
				// branding alone. With none published the title is the app's own
				// generic word ("Drive"), never an identity: a page's own `head`
				// still overrides it, and a document without any title at all
				// would fail WCAG 2.4.2 (axe `document-title`). It belongs
				// INSIDE `meta` even though it is not a meta
				// tag. `head()` accepts `links`, `scripts`, `meta` and `styles` and
				// nothing else (`router-core/route.d.ts`); the title is built by
				// scanning the meta array for an entry carrying a `title` key
				// (`react-router/headContentUtils.js`). Written as a sibling of
				// `meta` it type-checks — the array is typed `unknown` — and is
				// then dropped without a word.
				{
					title:
						productName ??
						translateApp(
							match.context.appLanguage,
							'common:app_sidebar.drive'
						)
				}
			],
			// The favicon is the runtime one, and only when published: with none
			// there is no `<link rel="icon">` at all, so the browser shows its own
			// default rather than any identity. It is a link to the instance's
			// stable asset URL, whose format the published file decides, so no
			// `type` is claimed.
			links: favicon ? [{ rel: 'icon', href: favicon }] : []
		}
	},
	shellComponent: RootDocument,
	component: RootComponent
})

function RootDocument({ children }: { children: ReactNode }) {
	const context = Route.useRouteContext()
	const { appLanguage, theme } = context
	const resolvedClass = resolveServerThemeClass(theme)

	return (
		<html
			className={cn('h-full', resolvedClass)}
			lang={appLanguage}
			style={resolvedClass ? { colorScheme: resolvedClass } : undefined}
			suppressHydrationWarning
		>
			<head>
				<HeadContent />
				<script
					// biome-ignore lint/security/noDangerouslySetInnerHtml: inline theme initializer must run synchronously before first paint
					dangerouslySetInnerHTML={{ __html: THEME_INIT_SCRIPT }}
				/>
			</head>
			<body className='h-full'>
				<AppProviders initialTheme={theme}>{children}</AppProviders>
				<Scripts />
			</body>
		</html>
	)
}

// Every route renders through this outlet. Phones are no longer turned
// away here (OPSSTD-9159): the client adapts to the width instead, area by
// area, and the operator console keeps its own gate.
function RootComponent() {
	return <Outlet />
}

function resolveServerThemeClass(theme: Theme) {
	if (theme === 'dark' || theme === 'light') {
		return theme
	}
	return
}

// Synchronous theme initializer. Runs in <head> before body paint so the
// `.dark` / `.light` class is on <html> before any CSS rule resolves a value
// from the theme tokens — no FOUC even in dev where Vite serves CSS via JS.
// CSP note: needs `script-src 'sha256-<HASH>'` (or `'unsafe-inline'`) on the
// deployed CSP header. Keep this script byte-for-byte stable to keep the hash valid.
const THEME_INIT_SCRIPT = `(()=>{try{const m=document.cookie.match(/(?:^|; )theme=([^;]*)/);const t=m?decodeURIComponent(m[1]):'';const r=t==='dark'||t==='light'?t:matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light';const h=document.documentElement;h.classList.add(r);h.style.colorScheme=r;h.style.backgroundColor=r==='dark'?'oklch(0.187 0.002 286)':'oklch(1 0 0)';}catch{}})();`

function AppProviders({
	children,
	initialTheme
}: {
	children: ReactNode
	initialTheme: Theme
}) {
	return (
		<ThemeProvider initialTheme={initialTheme}>
			<TooltipProvider>
				{/* Bottom right belongs to the uploads panel (OPSSTD-8745). */}
				<Toaster position='bottom-left' />
				{children}
			</TooltipProvider>
		</ThemeProvider>
	)
}

/**
 * THE FIVE CROSS-CUTTING STATES, AND WHO PRESENTS EACH (TD.3).
 *
 * Declared, not built: every one of these primitives already exists, and a
 * wrapper that renames one is not a state — it is a second name for it.
 *
 *   Loading         `<Spinner>` route-wide (below), `<Skeleton>` inside a
 *                   surface that knows its own shape
 *   Empty           `<Empty>` from `@repo/ui/empty`, at the surface
 *   Error           `<ErrorPage>` route-wide (below), `<Alert>` inside a
 *                   surface that can degrade without losing the page
 *   Permission      no producer yet — MS7. Denied routes redirect here and
 *   denied          show nothing; denial on a resource needs an API
 *                   that denies
 *   Realtime        no producer yet — MS9, both degrees, and the second one
 *   degraded        must be explicit rather than silent
 *
 * The two without a producer build nothing now: a component shaped for a state
 * no surface renders is a guess, and MS9 would have to reshape it anyway.
 *
 * ONE RULE THE THREE LIVE ONES MUST KEEP. Empty and loading must not be
 * confusable in any data-arrival order. What opens empty is "the read returned
 * AND there are no items", never "there are no items" — a surface that renders
 * off the initial array claims emptiness while the read is still in flight,
 * which is a lie and the most common way to write it.
 *
 * The route-level fallbacks below are wired in `router.tsx`. They are the last
 * resort, not the first: a surface that owns a state presents it itself, and a
 * boundary that intercepts a failure the surface could have shown erases a page
 * for what should have been a line inside it.
 */
export function NotFoundPage() {
	const { t } = useRouteTranslation()
	const navigate = useNavigate()

	return (
		<FallbackLayout
			actions={
				<Button onClick={() => navigate({ to: '/' })}>
					{t('common:app_fallbacks.go_home')}
				</Button>
			}
			description={t('common:app_fallbacks.not_found.description')}
			title={t('common:app_fallbacks.not_found.title')}
		/>
	)
}

export function LoadingPage() {
	const { t } = useRouteTranslation()

	return (
		<div className='grid min-h-full w-full place-items-center p-4'>
			<Spinner
				aria-label={t('common:app_fallbacks.loading')}
				className='size-6 text-primary'
			/>
		</div>
	)
}

export function ErrorPage({ error, reset }: ErrorComponentProps) {
	const { appLanguage, t } = useRouteTranslation()
	const { handleGoBack } = useFallbackNavigation()

	return (
		<FallbackLayout
			actions={
				<>
					<Button onClick={reset}>
						<RefreshCw data-icon='inline-start' />
						{t('common:app_fallbacks.try_again')}
					</Button>
					<Button onClick={handleGoBack} variant='outline'>
						{t('common:app_fallbacks.go_back')}
					</Button>
				</>
			}
			description={formatError(error, appLanguage)}
			title={t('common:app_fallbacks.error.title')}
		/>
	)
}

function useRouteTranslation() {
	const context = Route.useRouteContext()
	const { appLanguage } = context

	return {
		appLanguage,
		t: (key: TKey) => translateApp(appLanguage, key)
	}
}

/** Fills the route outlet, not the viewport: fallbacks render nested inside still-mounted parent layouts. */
function FallbackLayout({
	actions,
	description,
	title
}: {
	actions: ReactNode
	description: string
	title: string
}) {
	return (
		<main className='grid min-h-full w-full place-items-center p-6'>
			<Card className='w-full max-w-xl bg-transparent shadow-none ring-0'>
				<CardHeader className='gap-6'>
					<div className='flex size-12 items-center justify-center rounded-lg border border-destructive/20 bg-destructive/10 text-destructive'>
						<TriangleAlert aria-hidden='true' className='size-6' />
					</div>
					<CardTitle className='text-3xl'>{title}</CardTitle>
					<CardDescription className='max-w-lg text-base leading-7'>
						{description}
					</CardDescription>
				</CardHeader>
				<CardFooter className='flex-wrap gap-2'>{actions}</CardFooter>
			</Card>
		</main>
	)
}

function useFallbackNavigation() {
	const canGoBack = useCanGoBack()
	const navigate = useNavigate()
	const router = useRouter()

	return {
		handleGoBack: () => {
			if (canGoBack) {
				router.history.back()
				return
			}

			return navigate({ to: '/' })
		}
	}
}
